import type nl from "./nl";

const en: typeof nl = {
  nav: {
    home: "Home",
    portfolio: "Portfolio",
    whoAreWe: "Who Are We",
    contact: "Contact",
    getInTouch: "Get in Touch",
  },
  footer: {
    description:
      "Belgian engineering company specialised in programming, automation, project management, and integration engineering.",
    navigation: "Navigation",
    contact: "Contact",
    location: "Antwerp, Belgium",
    rights: "All rights reserved.",
    tagline: "Engineered with precision in Belgium",
    privacy: "Privacy Policy",
  },
  privacy: {
    kicker: "Privacy",
    title: "Privacy Policy",
    intro: "How TEKDAM BV handles your personal data.",
    lastUpdated: "Last updated: 11 June 2026",
    sections: [
      {
        heading: "Who are we?",
        paragraphs: [
          "TEKDAM BV (VAT: BE1024.973.363), based in Antwerp, Belgium, is the data controller for the personal data collected through this website. For any questions about this privacy policy, you can reach us at info@tekdam.be.",
        ],
      },
      {
        heading: "What data do we collect?",
        paragraphs: [
          "When you fill in our contact form, we collect the information you provide yourself: your first name, last name, email address, phone number (optional), and your message.",
          "In addition, this website stores your language preference (Dutch or English) locally in your browser. This preference never leaves your device and is not shared with us.",
        ],
      },
      {
        heading: "How do we use your data?",
        paragraphs: [
          "We use the data from the contact form solely to handle your enquiry and respond to you. The legal basis for this is your consent, given by submitting the form, and our legitimate interest in responding to business enquiries.",
          "We do not use your data for marketing and do not send newsletters.",
        ],
      },
      {
        heading: "Cookies and analytics",
        paragraphs: [
          "This website does not use tracking cookies or any analytics or advertising services. Only your language preference is stored locally in your browser so the website can be shown in your language.",
        ],
      },
      {
        heading: "Who do we share your data with?",
        paragraphs: [
          "We never sell or rent your data to third parties. Contact form messages are technically processed by FormSubmit, a service that delivers the form to our mailbox (info@tekdam.be). Beyond that, your data is only processed by our email provider.",
        ],
      },
      {
        heading: "How long do we keep your data?",
        paragraphs: [
          "We keep your message and contact details no longer than necessary to handle your enquiry and follow up on a potential collaboration, unless a legal obligation requires a longer retention period.",
        ],
      },
      {
        heading: "Your rights",
        paragraphs: [
          "Under the General Data Protection Regulation (GDPR), you have the right to access, rectify, erase, and port your personal data, as well as the right to restrict or object to its processing. You can exercise these rights by sending an email to info@tekdam.be.",
          "If you believe we are processing your data unlawfully, you can file a complaint with the Belgian Data Protection Authority (www.dataprotectionauthority.be).",
        ],
      },
      {
        heading: "Changes to this policy",
        paragraphs: [
          "We may update this privacy policy from time to time. The most recent version is always available on this page.",
        ],
      },
    ],
  },
  home: {
    hero: {
      titleLine1: "Where Technology",
      titlePre: "Meets ",
      titleHighlight: "Engineering",
      titlePost: "",
      paragraph:
        "Belgian engineering company specializing in software, automation, and system integration.",
      viewWork: "View Our Work",
      contactUs: "Contact Us",
    },
    services: {
      kicker: "What We Do",
      title: "Our Core Expertise",
      items: [
        {
          title: "Programming",
          description:
            "Full-stack software development, from web applications and APIs to embedded systems and industrial automation software.",
        },
        {
          title: "Project Engineering",
          description:
            "End-to-end project management for complex engineering initiatives, from concept and design through validation and delivery.",
        },
        {
          title: "Automation",
          description:
            "Industrial automation solutions including PLC programming, SCADA systems, and smart manufacturing for Industry 4.0 and 5.0.",
        },
        {
          title: "Integration",
          description:
            "Seamless integration between legacy and modern systems. Connecting databases, PLCs, SCADA, IoT, and web applications.",
        },
      ],
    },
    stats: [
      { value: "10+", label: "Systems Integrated" },
      { value: "20+", label: "Technologies Mastered" },
      { value: "100%", label: "Client Satisfaction" },
      { value: "5+", label: "Years Experience" },
    ],
    industries: {
      kicker: "Industries",
      title: "Bridging Legacy & Innovation",
      paragraph:
        "From pharmaceutical production lines built in the 1990s to cutting-edge Industry 5.0 IoT platforms, we connect systems across generations. Our expertise spans decades of industrial technology evolution.",
      items: [
        { title: "Pharmaceutical", description: "GMP-compliant systems, industrial applications, automation & validation." },
        { title: "Financial Services", description: "Custom web applications for CRM, dossier management, and client management. Automated flows." },
        { title: "Industrial IoT", description: "Sensor networks, Modbus TCP, MQTT, and real-time data platforms." },
        { title: "Legacy Integration", description: "Integrating older systems with modern applications and tools." },
      ],
      technologies: [
        ".NET / C#",
        "IoT / MQTT",
        "Modbus TCP",
        "PLC / SCADA",
        "Web Applications",
        "Industry 5.0",
        "Vue.js",
        "AI Agents",
      ],
    },
    cta: {
      title: "Ready to Start Your Project?",
      paragraph:
        "Let's discuss how TEKDAM Engineering can bring precision, reliability, and innovation to your next project.",
      button: "Get in Touch",
    },
  },
  about: {
    kicker: "About Us",
    title: "Who Are We?",
    intro:
      "A Belgian engineering company driven by precision, innovation, and the relentless pursuit of technical excellence.",
    mission: {
      kicker: "Our Mission",
      title: "Engineering Solutions That Last",
      paragraphs: [
        "TEKDAM Engineering is a Belgian-based engineering and technology company headquartered in Kontich, near Antwerp. We specialise in delivering robust, scalable technical solutions for industries where reliability isn't optional - it's essential.",
        "Our expertise covers the full spectrum of engineering: from hands-on PLC programming and SCADA system design to modern web application development and complex system integration. We bridge the gap between legacy industrial systems and today's cutting-edge technologies.",
        "Whether it's connecting a production line from the 1990s to a modern IoT platform, building a custom CRM for a financial services company, or designing GMP-compliant monitoring systems for pharmaceutical environments - we deliver solutions that work, today and tomorrow.",
      ],
    },
    values: [
      {
        title: "Precision",
        description: "Every detail matters. We engineer solutions with meticulous attention to quality and accuracy.",
      },
      {
        title: "Innovation",
        description: "We embrace new technologies while respecting proven methods that have stood the test of time.",
      },
      {
        title: "Reliability",
        description: "Our systems are built to run 24/7 in mission-critical environments, from cleanrooms to production floors.",
      },
      {
        title: "Partnership",
        description:
          "We work alongside our clients as true partners, understanding their business before writing a single line of code.",
      },
    ],
    capabilities: {
      kicker: "Capabilities",
      title: "What Sets Us Apart",
      skills: [
        {
          title: "Full-Stack Development",
          description:
            "Modern web applications using Vue.js, TypeScript, .NET/C#, and SQL Server. From concept to deployment, including Docker and CI/CD pipelines.",
        },
        {
          title: "Industrial Automation",
          description:
            "PLC programming, SCADA design, and integration with production lines. Experience with systems spanning three decades of industrial technology.",
        },
        {
          title: "IoT & Industry 5.0",
          description:
            "Sensor networks, Modbus TCP, MQTT messaging, and real-time data platforms connecting the physical world to intelligent software.",
        },
        {
          title: "System Integration",
          description:
            "Connecting legacy systems from the 1990s with modern platforms. Protocol translation, data bridging, and unified dashboards.",
        },
        {
          title: "Pharma Compliance",
          description:
            "GMP documentation, IQ/OQ/PQ validation, and compliance-ready solutions for Grade A, B, and C cleanroom environments.",
        },
        {
          title: "Project Management",
          description:
            "End-to-end project engineering, from requirements analysis and system design to testing, validation, and handover.",
        },
      ],
    },
    approach: {
      kicker: "Our Approach",
      title: "How We Work",
      steps: [
        {
          title: "Understand",
          description: "We dive deep into your business, processes, and technical landscape before proposing solutions.",
        },
        {
          title: "Design",
          description: "Architecture and system design that accounts for current needs and future growth.",
        },
        {
          title: "Build",
          description: "Precise engineering with rigorous testing and validation at every step.",
        },
        {
          title: "Deliver",
          description: "Deployment, documentation, training, and ongoing support to ensure lasting success.",
        },
      ],
    },
    cta: {
      title: "Let's Build Something Great",
      paragraph: "We're always open to discuss new projects, challenges, and partnerships.",
      contact: "Contact Us",
      work: "View Our Work",
    },
  },
  portfolio: {
    banner: {
      title: "Let's Build Something Great",
      paragraph: "We're always open to discuss new projects, challenges, and partnerships.",
      contact: "Contact Us",
      about: "Who Are We",
    },
    kicker: "Our Work",
    title: "Selected Projects",
    intro: "A curated selection of engineering projects across pharmaceutical manufacturing and financial technology - from production-line monitoring to custom enterprise platforms.",
    landing: {
      scroll: "Scroll",
      enter: "Enter",
    },
    industries: {
      pharma: {
        eyebrow: "Life Sciences",
        name: "Pharmaceutical Industry",
        tagline: "IT & automation consultancy for pharma",
        description:
          "We help pharmaceutical manufacturers design, build and validate the software, automation and monitoring systems that drive their production - bridging IT, OT and GMP compliance.",
        explore: "Explore pharmaceutical work",
        stats: [
          { value: "20+", label: "Integrated Systems" },
          { value: "2", label: "Site-wide Monitoring Systems" },
        ],
      },
      fintech: {
        eyebrow: "Financial Services",
        name: "Financial Technology",
        tagline: "Custom platforms for financial services",
        description:
          "From a personalised CRM that runs an entire credit company to a connected client website - we build secure, tailored fintech platforms that people rely on every day.",
        explore: "Explore financial work",
        stats: [
          { value: "2", label: "Platforms" },
        ],
      },
    },
    category: {
      back: "Back to industries",
      projectsTitle: "Selected Work",
      projectsIntro: "Projects delivered in this industry. Select one to dive into the details.",
    },
    capabilities: {
      title: "Additional Capabilities",
      intro: "More engineering services we deliver across pharmaceutical projects.",
    },
    filters: {
      all: "All",
      pharma: "Pharmaceutical",
      fintech: "Financial Tech",
    },
    categories: {
      pharma: "Pharmaceutical",
      fintech: "Financial Technology",
    },
    viewProject: "View project",
    projects: [
      {
        slug: "production-line-monitoring",
        title: "Production Line Grade A Monitoring",
        summary: "A unified Grade A monitoring layer connecting PLCs, SCADA, databases and web UIs across production lines built between 1990 and 2026.",
        overview:
          "A comprehensive monitoring system at production-line level that communicates with databases, PLCs, SCADA systems and web application interfaces - giving operators and quality teams a single, real-time view of the entire production floor.",
        challenge:
          "Production lines installed across three decades each spoke their own dialect: legacy PLCs from the 1990s alongside modern controllers, different databases, and isolated SCADA islands. There was no unified, real-time picture of production.",
        solution:
          "We engineered a central monitoring layer that talks to every system natively - old and new - normalises the data, and surfaces it through a clean web UI. Events, alarms and batch data flow into databases for traceability and reporting.",
        highlights: [
          "Single real-time view across all production lines",
          "Native communication with legacy & modern PLCs",
          "SCADA and database integration",
          "Full batch traceability and reporting",
        ],
      },
      {
        slug: "environmental-monitoring",
        title: "Site-wide Environmental Monitoring",
        summary: "An Industry 5.0 IoT platform streaming data from 10,000+ sensors across the entire site to a central web application used by stakeholders company-wide.",
        overview:
          "An Industry 5.0 environmental monitoring platform connecting more than 10,000 sensors to a centralised web application. Real-time data supports compliance, quality assurance and operational excellence across the organisation.",
        challenge:
          "Monitoring an entire site means ingesting data from tens of thousands of sensors reliably, presenting it to very different stakeholders - from operators to QA to management - and keeping everything auditable for compliance.",
        solution:
          "We built a scalable IoT data pipeline and a role-aware web application. Sensor data is collected, validated and stored at scale, with live dashboards, alerting and historical analysis tailored to each type of user.",
        highlights: [
          "10,000+ connected sensors",
          "Real-time dashboards & alerting",
          "Role-based views for every stakeholder",
          "Compliance-ready data history",
        ],
      },
      {
        slug: "fiber-sensor-integration",
        title: "Sensor & Fiber Integration",
        summary: "Reading Modbus TCP servers from fiber sensor networks and translating them into MQTT messages for modern web applications.",
        overview:
          "Fiber-wiring sensor systems that read Modbus TCP servers, translate industrial protocols into MQTT messages, and stream the data to modern web applications for real-time monitoring and analysis.",
        challenge:
          "Industrial fiber sensors expose their data over Modbus TCP - a protocol that doesn't fit modern, event-driven web architectures. The data had to reach web applications in real time without overloading the network.",
        solution:
          "We developed a protocol bridge that polls the Modbus TCP servers, transforms the readings, and republishes them as lightweight MQTT messages. Web applications subscribe and update instantly as new data arrives.",
        highlights: [
          "Modbus TCP to MQTT protocol bridge",
          "Real-time, event-driven data flow",
          "Lightweight and network-efficient",
          "Plugs straight into web applications",
        ],
      },
      {
        slug: "gmp-validation",
        title: "GMP Documentation & Validation",
        summary: "Complete GMP documentation, validation protocols and IQ/OQ/PQ testing for regulated pharmaceutical environments.",
        overview:
          "End-to-end pharmaceutical GMP documentation services - validation protocols, test scripts, IQ/OQ/PQ documentation and compliance reporting - for systems operating in regulated environments.",
        challenge:
          "In regulated pharma, software and equipment are only as good as their documentation. Every system needs validated, auditable proof that it does exactly what it should, under GMP scrutiny.",
        solution:
          "We deliver the full validation lifecycle: writing protocols, executing IQ/OQ/PQ test scripts, and producing the compliance documentation that stands up to audits and inspections.",
        highlights: [
          "Validation protocols & test scripts",
          "IQ / OQ / PQ execution",
          "Audit- and inspection-ready documentation",
          "Full GMP compliance reporting",
        ],
      },
      {
        slug: "electrical-engineering",
        title: "Electrical Engineering",
        summary: "Electrical drawings and schematics for industrial installations, compliant with pharmaceutical standards.",
        overview:
          "Electrical drawings and schematics for industrial installations - designed to comply with pharmaceutical standards and to integrate seamlessly with automation and monitoring systems.",
        challenge:
          "Industrial installations in pharma need precise, standards-compliant electrical documentation that ties cleanly into the automation systems controlling them.",
        solution:
          "We produce accurate electrical drawings and schematics that meet the applicable standards and align with the automation architecture, so installation and integration run smoothly.",
        highlights: [
          "Standards-compliant electrical drawings",
          "Detailed schematics for installations",
          "Aligned with automation systems",
          "Ready for integration & handover",
        ],
      },
      {
        slug: "cleanroom-applications",
        title: "Cleanroom Applications",
        summary: "Industrial applications engineered for Grade A, B and C cleanroom environments and sterile manufacturing.",
        overview:
          "Industrial applications designed specifically for Grade A, B and C cleanroom environments - meeting the strictest pharmaceutical manufacturing requirements for sterile and controlled areas.",
        challenge:
          "Sterile and controlled manufacturing zones impose the highest requirements on every application that runs in them. Nothing can compromise the integrity of a Grade A, B or C environment.",
        solution:
          "We engineer applications that respect cleanroom constraints end-to-end - built and documented to meet the requirements of sterile, controlled manufacturing.",
        highlights: [
          "Engineered for Grade A / B / C zones",
          "Sterile manufacturing compliance",
          "Built for controlled environments",
          "Integrated with monitoring & automation",
        ],
      },
      {
        slug: "haspro",
        title: "HasPro - CRM Platform",
        summary: "A personalised CRM and management platform that is the backbone of a Belgian credit company operating since 1995.",
        overview:
          "A personalised web application serving as the operational backbone of a Belgian credit company. HasPro manages contacts, documents, news, audit trails and dossiers for home loans, consumer credit and insurances - used daily by the owner, employees and partners.",
        challenge:
          "A credit company active since 1995 had outgrown generic tools. They needed one tailored system to manage clients, documents and dossiers - with the audit trails and compliance a financial business demands.",
        solution:
          "We designed and built HasPro from the ground up: a custom CRM where contacts, documents, dossiers and communications live together, with role-based access for the owner, employees and partners and full audit logging.",
        highlights: [
          "Contact & client management",
          "Document management system",
          "News articles & communications",
          "Audit trail & compliance logging",
          "Dossier management (HL, LOA, Insurance)",
          "Multi-user: owner, employees & partners",
        ],
      },
      {
        slug: "haspro-client-website",
        title: "HasWeb - Client Website",
        summary: "A modern, multilingual client website connected directly to the internal HasPro platform.",
        overview:
          "A modern, design-focused website built for the credit company's clients. Fully connected to the internal HasPro platform, it bridges clients and internal operations - with loan simulators, contact forms and multi-language support.",
        challenge:
          "The company wanted a polished public face that wasn't just a brochure - it had to connect real clients to the internal HasPro system and let them simulate loans and get in touch effortlessly.",
        solution:
          "We built a fast, responsive, multilingual website linked to HasPro. Visitors can simulate loans, submit enquiries that route straight into the internal system, all tracked with privacy-friendly analytics.",
        highlights: [
          "Modern responsive design",
          "Connected to internal HasPro system",
          "Multi-language (NL, FR, EN, TR)",
          "Loan simulators & calculators",
          "Contact forms with direct routing",
          "Privacy-friendly analytics",
        ],
      },
    ],
    detail: {
      back: "Back to portfolio",
      overview: "Overview",
      challenge: "The Challenge",
      solution: "Our Solution",
      highlights: "Highlights",
      tech: "Technologies",
      notFound: "Project not found.",
      other: "Explore more projects",
      cta: {
        title: "Interested in a similar solution?",
        paragraph: "Let's talk about how we can bring the same engineering rigour to your project.",
        button: "Get in Touch",
      },
    },
    cta: {
      title: "Have a Project in Mind?",
      paragraph:
        "Whether you need monitoring systems, custom web applications, or complex system integration, we're ready to help.",
      button: "Start a Conversation",
    },
  },
  contact: {
    kicker: "Get in Touch",
    title: "Contact",
    intro: "Ready to discuss your next project? We'd love to hear from you.",
    form: {
      heading: "Send Us a Message",
      firstName: "First Name",
      firstNamePlaceholder: "John",
      lastName: "Last Name",
      lastNamePlaceholder: "Doe",
      email: "Email",
      emailPlaceholder: "john@example.com",
      phone: "Phone",
      phonePlaceholder: "+32 ...",
      message: "Message",
      messagePlaceholder: "Tell us about your project...",
      success: "Thank you! We've received your message and will get back to you soon.",
      error: "Something went wrong. Please try again or email us directly.",
      send: "Send Message",
      sending: "Sending...",
    },
    details: {
      heading: "Company Details",
      location: "Antwerp, Belgium",
    },
    next: {
      heading: "What Happens Next?",
      steps: [
        "We review your message and understand your requirements.",
        "We schedule a call to discuss your project in detail.",
        "We provide a tailored proposal and project roadmap.",
      ],
    },
  },
};

export default en;
